import { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  CreditCard,
  Calendar,
  ExternalLink,
  ArrowRight,
  ArrowLeft,
  Clock,
  CalendarCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SectionHeader from "@/components/site/SectionHeader";

// Build 30-minute time slots from 9:00 AM to 7:00 PM
function buildTimeSlots() {
  const slots = [];
  for (let m = 9 * 60; m <= 19 * 60; m += 30) {
    const h24 = Math.floor(m / 60);
    const min = m % 60;
    const ampm = h24 >= 12 ? "PM" : "AM";
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    slots.push(`${h12}:${String(min).padStart(2, "0")} ${ampm}`);
  }
  return slots;
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

function isWeekday(dateStr) {
  if (!dateStr) return false;
  const [y, m, d] = dateStr.split("-").map(Number);
  const day = new Date(y, m - 1, d).getDay(); // 0 Sun .. 6 Sat
  return day >= 1 && day <= 5;
}

export default function IntakeNext() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const intakeId = location.state?.intakeId || null;

  const [pkg, setPkg] = useState(null);
  const [mode, setMode] = useState("choose"); // choose | schedule | done
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const slots = useMemo(buildTimeSlots, []);

  useEffect(() => {
    let cancel = false;
    api
      .get(`/packages/${packageId}/deposit-link`)
      .then(({ data }) => {
        if (!cancel) setPkg(data);
      })
      .catch(() => {
        toast.error("Unknown package");
        navigate("/packages", { replace: true });
      });
    return () => {
      cancel = true;
    };
  }, [packageId, navigate]);

  if (!pkg) {
    return <div className="py-32 text-center text-gray-400">Loading…</div>;
  }

  const handleBack = () => {
    if (mode === "schedule") setMode("choose");
    else navigate(`/intake/${packageId}`);
  };

  const continueWithPayment = async () => {
    try {
      setBusy(true);
      const origin = window.location.origin;
      const { data } = await api.post("/payments/deposit-checkout", {
        origin_url: origin,
        intake_id: intakeId,
        package_id: packageId,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const submitSchedule = async () => {
    if (!date) return toast.error("Please choose a date.");
    if (!isWeekday(date)) return toast.error("Please choose a weekday (Monday–Friday).");
    if (!time) return toast.error("Please choose a time.");
    try {
      setBusy(true);
      if (intakeId) {
        await api.post(`/intakes/${intakeId}/finalize`, {
          path: "consultation",
          consultation_date: date,
          consultation_time: time,
        });
      }
      toast.success("Consultation scheduled! We'll reach out to confirm.");
      setMode("done");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not schedule. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-intake-next">
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 eq-grid-bg" />
        <div className="eq-aurora" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-6 text-blue-300">
            <CheckCircle2 className="w-5 h-5" />
            <span className="text-sm uppercase tracking-widest">Project form received</span>
          </div>

          {mode === "done" ? (
            <div
              className="glass-strong rounded-3xl p-10 md:p-14 text-center"
              data-testid="intake-next-done"
            >
              <div className="w-16 h-16 rounded-2xl bg-green-500/15 border border-green-400/40 grid place-items-center text-green-300 mx-auto shadow-[0_0_30px_rgba(34,197,94,0.3)]">
                <CalendarCheck className="w-7 h-7" strokeWidth={1.7} />
              </div>
              <h2 className="mt-6 font-display text-3xl font-semibold tracking-tight">
                You're all set!
              </h2>
              <p className="mt-3 text-gray-400 max-w-lg mx-auto leading-relaxed">
                Your project details have been sent to our team. A representative will reach out to
                you about your project details shortly during business hours
                (Mon–Fri, 9:00 AM–7:00 PM).
              </p>
              <button
                onClick={() => navigate("/")}
                data-testid="intake-next-home-btn"
                className="btn-ghost mt-8"
              >
                Back to Home
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={handleBack}
                data-testid="intake-next-back-btn"
                className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> {mode === "schedule" ? "Back to options" : "Back to project form"}
              </button>

              <SectionHeader
                eyebrow="Final step"
                title={`Start your ${pkg.name} project`}
                description="Choose how you'd like to move forward. Continue with payment to begin immediately, or schedule a project consultation call first."
              />

              <div className="grid md:grid-cols-2 gap-6">
                {/* Continue with Payment */}
                <div className="rounded-3xl p-8 bg-gradient-to-b from-blue-500/10 to-white/5 border border-blue-500/40 shadow-[0_0_40px_-12px_rgba(37,99,235,0.5)] backdrop-blur-xl flex flex-col">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 border border-blue-500/40 grid place-items-center text-blue-200">
                    <CreditCard className="w-5 h-5" strokeWidth={1.7} />
                  </div>
                  <h3 className="mt-5 font-display text-2xl font-semibold">Continue with Payment</h3>
                  <p className="mt-2 text-sm text-gray-400 leading-relaxed">
                    Pay your 10% non-refundable deposit and have your project started immediately.
                  </p>
                  <div className="mt-4 text-sm">
                    <div className="flex items-center justify-between py-2 border-b border-white/10">
                      <span className="text-gray-400">Package</span>
                      <span className="text-white">{pkg.name}</span>
                    </div>
                    {pkg.price != null && (
                      <div className="flex items-center justify-between py-2 border-b border-white/10">
                        <span className="text-gray-400">Full Price</span>
                        <span className="text-white">${pkg.price?.toFixed(2)}</span>
                      </div>
                    )}
                    {pkg.deposit != null && (
                      <div className="flex items-center justify-between py-2">
                        <span className="text-gray-400">10% Deposit</span>
                        <span className="text-white font-semibold">${pkg.deposit?.toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={continueWithPayment}
                    disabled={busy}
                    data-testid="next-pay-deposit-btn"
                    className="btn-primary w-full mt-6 disabled:opacity-60"
                  >
                    {busy ? "Processing…" : pkg.deposit != null ? `Continue with Payment` : "Continue with Payment"}
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>

                {/* Schedule Project Consultation */}
                <div className="rounded-3xl p-8 bg-white/[0.04] border border-white/10 backdrop-blur-xl flex flex-col">
                  <div className="w-12 h-12 rounded-xl bg-white/10 border border-white/20 grid place-items-center text-blue-200">
                    <Calendar className="w-5 h-5" strokeWidth={1.7} />
                  </div>
                  <h3 className="mt-5 font-display text-2xl font-semibold">
                    Schedule Project Consultation
                  </h3>
                  <p className="mt-2 text-sm text-gray-400 leading-relaxed">
                    Prefer to talk first? Pick a date and time and we'll call you to discuss your
                    project before any payment.
                  </p>

                  {mode === "schedule" ? (
                    <div className="mt-5 space-y-4" data-testid="schedule-form">
                      <div>
                        <Label className="text-sm text-gray-300">Preferred Date (Mon–Fri)</Label>
                        <input
                          type="date"
                          min={todayStr()}
                          value={date}
                          onChange={(e) => setDate(e.target.value)}
                          data-testid="schedule-date-input"
                          className="mt-1.5 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-white text-sm [color-scheme:dark] focus:outline-none"
                        />
                        {date && !isWeekday(date) && (
                          <p className="mt-1 text-xs text-amber-300">
                            Please choose a weekday (Monday–Friday).
                          </p>
                        )}
                      </div>
                      <div>
                        <Label className="text-sm text-gray-300">Preferred Time (9:00 AM–7:00 PM)</Label>
                        <Select value={time} onValueChange={setTime}>
                          <SelectTrigger
                            data-testid="schedule-time-select"
                            className="mt-1.5 bg-white/5 border-white/10 text-white"
                          >
                            <SelectValue placeholder="Select a time slot" />
                          </SelectTrigger>
                          <SelectContent className="max-h-64">
                            {slots.map((s) => (
                              <SelectItem key={s} value={s}>
                                <span className="inline-flex items-center gap-2">
                                  <Clock className="w-3.5 h-3.5 text-blue-400" /> {s}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <button
                        onClick={submitSchedule}
                        disabled={busy}
                        data-testid="schedule-submit-btn"
                        className="btn-primary w-full disabled:opacity-60"
                      >
                        {busy ? "Scheduling…" : "Confirm Consultation"}
                        <CalendarCheck className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <ul className="mt-5 space-y-2 text-sm text-gray-300">
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-blue-400" /> Free — no payment required
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-blue-400" /> Weekdays, 9:00 AM–7:00 PM
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-blue-400" /> Payment link sent only if you proceed
                        </li>
                      </ul>
                      <button
                        onClick={() => setMode("schedule")}
                        data-testid="next-book-consult-btn"
                        className="btn-ghost w-full mt-6"
                      >
                        Schedule Project Consultation <ArrowRight className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Notes under the buttons */}
              <div
                className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6 space-y-3"
                data-testid="intake-next-notes"
              >
                <p className="text-sm text-gray-300 leading-relaxed flex gap-3">
                  <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                  The consultation is free — a payment link is sent after the call if we agree on the
                  project. Or continue with payment and have your project started immediately.
                </p>
                <p className="text-sm text-gray-300 leading-relaxed flex gap-3">
                  <Clock className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                  A representative will reach out to you about project details shortly during business
                  hours.
                </p>
              </div>

              <div className="mt-8 text-center text-sm text-gray-500">
                Questions? Email{" "}
                <a className="text-blue-300 hover:text-white" href="mailto:Equinox@freedomcoreventures.com">
                  Equinox@freedomcoreventures.com
                </a>
                .
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
